import React, { useReducer, useEffect, useState, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import reducer from '../../../../store/componentReducer';
import { init, setValue } from '../../../../store/actions';
import { updateAt, removeAt, reorder } from './MainNavComponents/navEditorState';
import NavLangStackEditor from './MainNavComponents/NavLangStackEditor';
import getSearchPosts from '../../../../api/graphql/queries/searchPosts';

const initialState = {
  data: {
    type: 'RegistrationConfig',
    name: 'registrationConfig',
    registration: { artists: [], genres: [], festivals: [] },
  },
};

const AVATAR_COLORS = ['#d76d5f', '#4f5d75', '#7a9e7e', '#b0793f', '#6b5b95', '#3f7cac'];
const avatarColor = (slug) => {
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) hash = (hash * 31 + slug.charCodeAt(i)) % 997;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};
const initials = slug => slug.split('-').slice(0, 2).map(w => (w[0] || '').toUpperCase()).join('');

const ArtistThumb = ({ slug, image }) => (image
  ? <img className="mnv-rgc-thumb" src={image} alt="" />
  : <span className="mnv-rgc-thumb" style={{ background: avatarColor(slug) }}>{initials(slug)}</span>
);

ArtistThumb.propTypes = { slug: PropTypes.string.isRequired, image: PropTypes.string };
ArtistThumb.defaultProps = { image: null };

// Mirror editor for the registration-config reusable component: the signup
// onboarding payload (suggested artist slugs, genre options, festival scope
// options) shared by the web header (injected via { key } reference) and the
// native apps (expanded by getAppConfig). Rank = order shown during signup;
// all three lists are vertical rbd droppables with live rank badges.
const RegistrationConfig = ({ components, handlerSetComponents, id }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [editing, setEditing] = useState(null); // { list: 'genres'|'festivals', index }
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = closed, [] = no matches
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const { data } = state;

  const componentIndex = components.findIndex(obj => obj.id === id);
  const { component } = components[componentIndex];

  useEffect(() => {
    if (component) init(dispatch)(component);
    return () => clearTimeout(searchTimer.current);
  }, []);

  useEffect(() => {
    handlerSetComponents({ id, component: data }, componentIndex);
  }, [data]);

  const registration = data.registration || { artists: [], genres: [], festivals: [] };
  const artists = registration.artists || [];
  const patch = next => setValue(dispatch)('registration', { ...registration, ...next });

  // Artist picker: search posts by title, keep only the artist category.
  const runSearch = (term) => {
    setSearching(true);
    getSearchPosts(term, 'en', true).then((posts) => {
      setSearching(false);
      setResults((posts || [])
        .filter(p => p && (p.postCategory || []).indexOf('artist') > -1)
        .slice(0, 8)
        .map(p => ({
          slug: p.slugName,
          title: (p.postDescription && p.postDescription.title && p.postDescription.title.en) || p.slugName,
          image: (p.postDescription && p.postDescription.image && p.postDescription.image.en) || null,
        })));
    });
  };

  const onQueryChange = (value) => {
    setQuery(value);
    clearTimeout(searchTimer.current);
    if (value.trim().length < 3) { setResults(null); return; }
    searchTimer.current = setTimeout(() => runSearch(value.trim()), 300);
  };

  const addArtist = (slug) => {
    if (artists.indexOf(slug) === -1) patch({ artists: artists.concat(slug) });
    setQuery('');
    setResults(null);
  };

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;
    const listKey = destination.droppableId.replace('rgc-', '');
    patch({ [listKey]: reorder(registration[listKey] || [], source.index, destination.index) });
  };

  const rankedRow = (p, snapshot, children) => (
    <div
      ref={p.innerRef}
      {...p.draggableProps}
      className={`mnv-rgc-row${snapshot.isDragging ? ' mnv-rgc-row--dragging' : ''}`}
    >
      <span className="mnv-grip" {...p.dragHandleProps}>⋮⋮</span>
      {children}
    </div>
  );

  const optionList = (listKey, label, addLabel, valuePlaceholder) => {
    const list = registration[listKey] || [];
    return (
      <div className="mnv-rgc-section">
        <span className="mnv-lab">{label}</span>
        <Droppable droppableId={`rgc-${listKey}`} type={`RGC-${listKey}`}>
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="mnv-rgc-rail">
              {list.map((option, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={index}>
                  <Draggable draggableId={`rgc-${listKey}--${index}`} index={index}>
                    {(p, snapshot) => rankedRow(p, snapshot, (
                      <>
                        <span className="mnv-rgc-rank">{index + 1}</span>
                        <button type="button" className="mnv-rgc-main" onClick={() => setEditing(editing && editing.list === listKey && editing.index === index ? null : { list: listKey, index })}>
                          <span className="mnv-rgc-name">{(option.label && option.label.en) || '(empty)'}</span>
                          <span className="mnv-rgc-value">{option.value || ''}</span>
                        </button>
                        <button
                          type="button"
                          className="mnv-rgc-x"
                          title="Remove"
                          onClick={() => {
                            // eslint-disable-next-line no-alert
                            if (window.confirm('Remove this entry?')) {
                              setEditing(null);
                              patch({ [listKey]: removeAt(list, index) });
                            }
                          }}
                        >
                          ✕
                        </button>
                      </>
                    ))}
                  </Draggable>
                  {editing && editing.list === listKey && editing.index === index && (
                    <NavLangStackEditor
                      label={addLabel.toUpperCase()}
                      text={option.label}
                      onChangeText={next => patch({ [listKey]: updateAt(list, index, { ...option, label: next }) })}
                      url={option.value || ''}
                      onChangeUrl={next => patch({ [listKey]: updateAt(list, index, { ...option, value: next }) })}
                      urlLabel="VALUE"
                      urlPlaceholder={valuePlaceholder}
                      onDelete={() => { setEditing(null); patch({ [listKey]: removeAt(list, index) }); }}
                      onDone={() => setEditing(null)}
                    />
                  )}
                </Fragment>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <button
          type="button"
          className="mnv-add"
          onClick={() => {
            patch({ [listKey]: list.concat({ label: { en: '' }, value: '' }) });
            setEditing({ list: listKey, index: list.length });
          }}
        >
          {`+ ${addLabel}`}
        </button>
      </div>
    );
  };

  return (
    <div className="flex-1 mr-4 mnv">
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="mnv-rgc-section">
          <span className="mnv-lab">Suggested artists — order = priority in the signup follow step</span>
          <Droppable droppableId="rgc-artists" type="RGC-artists">
            {provided => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="mnv-rgc-rail">
                {artists.map((slug, index) => (
                  <Draggable draggableId={`rgc-artists--${slug}`} index={index} key={slug}>
                    {(p, snapshot) => rankedRow(p, snapshot, (
                      <>
                        <span className="mnv-rgc-rank">{index + 1}</span>
                        <ArtistThumb slug={slug} />
                        <span className="mnv-rgc-value mnv-rgc-value--left">{slug}</span>
                        <button
                          type="button"
                          className="mnv-rgc-x"
                          title="Remove"
                          onClick={() => patch({ artists: artists.filter(a => a !== slug) })}
                        >
                          ✕
                        </button>
                      </>
                    ))}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <div className="mnv-rgc-picker">
            <input
              type="text"
              className="mnv-input"
              value={query}
              placeholder="🔍 Search artists by name (min. 3 characters)…"
              onChange={e => onQueryChange(e.target.value)}
            />
            {results !== null && (
              <div className="mnv-rgc-dropdown">
                {results.map(r => (
                  <button
                    type="button"
                    key={r.slug}
                    className="mnv-rgc-opt"
                    disabled={artists.indexOf(r.slug) > -1}
                    onClick={() => addArtist(r.slug)}
                  >
                    <ArtistThumb slug={r.slug} image={r.image} />
                    <span className="mnv-rgc-name">{r.title}</span>
                    <span className="mnv-rgc-value">{r.slug}</span>
                    {artists.indexOf(r.slug) > -1 && <span className="mnv-rgc-added">added</span>}
                  </button>
                ))}
                {results.length === 0 && <div className="mnv-rgc-hint">{searching ? 'Searching…' : 'No artists match'}</div>}
                <div className="mnv-rgc-hint">Searching posts in category “artist”</div>
              </div>
            )}
          </div>
        </div>

        {optionList('genres', 'Genres — options in the signup taste step', 'genre', 'rock')}
        {optionList('festivals', 'Festivals — newsletter/scope options in signup (value = scope_*)', 'festival', 'scope_bcn')}
      </DragDropContext>
    </div>
  );
};

RegistrationConfig.propTypes = {
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default RegistrationConfig;
